// KpiCard — 21st.dev-inspired KPI-Kachel mit tone-Varianten + Corner-Pulse +
// optional Trend-Icon + delta-Sub-Line.
//
// Verwendet im Hyperliquid Wallet-Tracker-Tab (4 Karten: Account / Margin /
// Free Margin / uPnL). Tone steuert Border-Glow, Label-Color, Value-Color +
// Corner-Patch-Color.

import { Sparkles, TrendingDown, TrendingUp, Zap } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

export type KpiTone = 'neutral' | 'amber' | 'emerald' | 'rose';

interface KpiCardProps {
  label: string;
  value: ReactNode;
  /** Sub-Line unter dem Value (Trend-Hint, Delta-Δ, Erklär-Text). */
  sub?: ReactNode;
  /** Optional Trend-Direction (up = emerald icon, down = rose icon). */
  trend?: 'up' | 'down' | null;
  /** Tone steuert Glow + Color-Akzente. Default neutral. */
  tone?: KpiTone;
  /** Optional Icon vor dem Label (z.B. Zap für Margin Used). */
  icon?: LucideIcon;
  /** Ring-Highlight (true = ring-1 in tone-Farbe). Für „featured" Karte. */
  highlight?: boolean;
}

interface ToneStyles {
  labelColor: string;
  valueColor: string;
  cornerBg: string;
  ringClass: string;
}

const TONES: Record<KpiTone, ToneStyles> = {
  neutral: {
    labelColor: 'text-neutral-500',
    valueColor: 'text-white',
    cornerBg: 'bg-neutral-500/10',
    ringClass: 'ring-neutral-500/20',
  },
  amber: {
    labelColor: 'text-amber-400',
    valueColor: 'text-amber-400',
    cornerBg: 'bg-amber-500/15',
    ringClass: 'ring-amber-500/30',
  },
  emerald: {
    labelColor: 'text-emerald-400',
    valueColor: 'text-emerald-400',
    cornerBg: 'bg-emerald-500/15',
    ringClass: 'ring-emerald-500/30',
  },
  rose: {
    labelColor: 'text-rose-400',
    valueColor: 'text-rose-400',
    cornerBg: 'bg-rose-500/15',
    ringClass: 'ring-rose-500/30',
  },
};

export function KpiCard({
  label,
  value,
  sub,
  trend = null,
  tone = 'neutral',
  icon: Icon,
  highlight = false,
}: KpiCardProps) {
  const t = TONES[tone];
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : null;
  const trendColor =
    trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-rose-400' : '';

  return (
    <div
      className={`glass-strong relative overflow-hidden rounded-xl p-3 ${
        highlight ? `ring-1 ${t.ringClass}` : ''
      }`}
    >
      <div className={`kpi-corner absolute top-0 right-0 size-12 rounded-bl-full ${t.cornerBg}`} />
      <div className="relative">
        <div
          className={`mb-1 flex items-center gap-1 text-[11px] uppercase tracking-wider ${t.labelColor}`}
        >
          {Icon ? <Icon className="size-3" /> : null}
          <span>{label}</span>
        </div>
        <div className={`text-xl font-bold tabular ${t.valueColor}`}>{value}</div>
        {sub ? (
          <div className="mt-0.5 flex items-center gap-1 text-[11px] text-neutral-500">
            {TrendIcon ? <TrendIcon className={`size-3 ${trendColor}`} /> : null}
            {sub}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// Re-Exports für Convenience im Caller (Wallet-Tracker setzt z.B. Sparkles für uPnL).
export { Sparkles, Zap };
